import type { WebsiteContent } from './website';

/**
 * MusicGroup structured data for the About page. Keeping the entity details
 * consistent with the homepage is what lets Google tie the band, its members,
 * its hometown, and its streaming profiles together as one artist.
 */
export function buildAboutStructuredData(content: WebsiteContent) {
  const siteUrl = content.siteUrl.trim().replace(/\/$/, '');
  const sameAs = [
    content.instagramUrl,
    content.tiktokUrl,
    content.spotifyUrl,
    content.youtubeUrl,
    content.musicSecondaryUrl,
  ]
    .map((url) => url.trim())
    .filter(Boolean);

  const genres = content.seoGenre
    .split(/[,·|]/)
    .map((genre) => genre.trim())
    .filter(Boolean);

  const awards = content.aboutAwards
    .filter((award) => award.title.trim())
    .map((award) => [award.label.trim(), award.title.trim()].filter(Boolean).join(' — '));

  const members = content.aboutMembers
    .filter((member) => member.name.trim())
    .map((member) => ({
      '@type': 'OrganizationRole',
      member: { '@type': 'Person', name: member.name.trim() },
      ...(member.role.trim() ? { roleName: member.role.trim() } : {}),
    }));

  const performedAt = content.aboutVenues
    .filter((venue) => venue.venue.trim())
    .map((venue) => ({
      '@type': 'Place',
      name: venue.venue.trim(),
      ...(venue.city.trim() ? { address: venue.city.trim() } : {}),
    }));

  return {
    '@context': 'https://schema.org',
    '@type': 'MusicGroup',
    name: content.bandName,
    description: content.aboutSeoDescription || content.seoDescription,
    ...(genres.length ? { genre: genres } : {}),
    ...(siteUrl ? { url: siteUrl, mainEntityOfPage: `${siteUrl}/about` } : {}),
    ...(content.aboutShareImageUrl || content.aboutHeroImageUrl || content.shareImageUrl
      ? { image: content.aboutShareImageUrl || content.aboutHeroImageUrl || content.shareImageUrl }
      : {}),
    ...(content.seoLocation
      ? { location: { '@type': 'Place', name: content.seoLocation }, foundingLocation: { '@type': 'Place', name: 'College Station, Texas' } }
      : {}),
    ...(members.length ? { member: members } : {}),
    ...(awards.length ? { award: awards } : {}),
    ...(performedAt.length ? { subjectOf: performedAt } : {}),
    ...(sameAs.length ? { sameAs } : {}),
    ...(content.bookingEmail.trim() ? { email: content.bookingEmail.trim() } : {}),
  };
}
